import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

function LineAreaCombo({
  annotations,
  casesData,
  incidentData,
  countries,
  labels,
  dimensions,
}) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const wrapDim = useResizeObserver(wrapperRef);

  let { height, margin = {} } = dimensions;

  const baseStrokeWidth = 2.5;
  const highlightStrokeWidth = 3.5;
  const transDuration = 500;

  let dropdownDiv = d3.select(".dropdown__list");
  let options = d3.select("#showOptions");
  options.on("click", () => {
    var visible = dropdownDiv.style("display");
    if (visible == "none") dropdownDiv.style("display", "block");
    else dropdownDiv.style("display", "none");
  });

  let dropdownItem = d3.selectAll(".dropdown__link");
  let selectedOption = "optionAll";

  dropdownItem.on("click", (event, d) => {
    const item = d3.select(event.target);
    selectedOption = item.attr("id");

    dropdownItem.classed("selected", false);
    item.classed("selected", true);

    dropdownDiv.style("display", "none");
    options.text(item.text());

    changeViewOptions();
  });

  function changeViewOptions() {
    if (selectedOption === "optionAll") {
      d3.select(".area-group")
        .transition()
        .duration(transDuration)
        .style("opacity", 1);
      d3.select(".line-group")
        .transition()
        .duration(transDuration)
        .style("opacity", 1);
    } else if (selectedOption === "optionIncidents") {
      d3.select(".area-group")
        .transition()
        .duration(transDuration)
        .style("opacity", 1);
      d3.select(".line-group")
        .transition()
        .duration(transDuration)
        .style("opacity", 0);
    } else {
      d3.select(".area-group")
        .transition()
        .duration(transDuration)
        .style("opacity", 0);
      d3.select(".line-group")
        .transition()
        .duration(transDuration)
        .style("opacity", 1);
    }
  }

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    d3.select(".legend").selectChildren().remove();
    svg.selectAll("*").remove();
    if (!wrapDim) return;

    let width = wrapDim.width;

    svg
      .attr("height", height)
      .attr("width", width)
      .attr("xmlns", "http://www.w3.org/2000/svg")
      // .attr('xmlns', "http://www.w3.org/2000/svg")
      .attr("xmlns:xhtml", "http://www.w3.org/1999/xhtml");

    // version="1.1" xmlns= xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xhtml="http://www.w3.org/1999/xhtml"

    const relaxFactor = 1.1;
    const tooltipWidth = 360;
    const tooltipHeight = 210;

    const colors = [
      "#1E2748",
      "#A89659",
      "#7EB3C6",
      "#684B7C",
      "#7E8491",
      // "#808492",
      "#60A17D",
    ];
    const curve = d3.curveCatmullRom;

    var defs = svg.append("defs");

    var filter = defs
      .append("filter")
      .attr("id", "drop-shadow")
      .attr("height", "130%");

    filter
      .append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 22)
      .attr("result", "blur");

    filter
      .append("feOffset")
      .attr("in", "blur")
      // .attr("dx", 5)
      .attr("dy", 5)
      .attr("result", "offsetBlur");

    var feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "offsetBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    const mainGroup = svg.append("g");

    const areaGroup = mainGroup.append("g").attr("class", "area-group");
    const lineGroup = mainGroup.append("g").attr("class", "line-group");
    const axisGroup = mainGroup.append("g").attr("class", "axis");

    const annotationsGroup = mainGroup
      .append("g")
      .attr("class", "annotations-group");

    const flagLine = annotationsGroup
      .append("line")
      .attr("x1", 0)
      .attr("x2", 0)
      .attr("y1", height - margin.bottom)
      .attr("y2", height - margin.bottom - 1.5 * tooltipHeight)
      .attr("stroke", "#CACACA")
      .attr("stroke-width", 1.5)
      .attr("opacity", 0);

    const legendGroup = mainGroup
      .append("g")
      .attr("transform", `translate(${margin.left},0)`);

    var maxCases = 0;
    countries.forEach((country) => {
      const countryMaxCases = d3.max(casesData, (d) => d[country]);

      maxCases = countryMaxCases > maxCases ? countryMaxCases : maxCases;
    });

    const incidentDataStacked = incidentData?.map((d) => {
      var countryMap = new Map();

      countries.forEach((country) => {
        countryMap.set(country, d[country]);
      });

      return [d.date, countryMap];
    });

    const series = d3
      .stack()
      .keys(countries)
      .value(([, incidentDataStacked], key) => incidentDataStacked.get(key))
      .order(d3.stackOrderNone)(incidentDataStacked);

    const dateExtent = d3.extent(casesData, (d) => d.date);

    var startDate = new Date(dateExtent[0].getTime());
    startDate.setDate(startDate.getDate() - 15);

    var endDate = new Date(dateExtent[1].getTime());
    endDate.setDate(endDate.getDate() + 15);

    const x = d3
      .scaleUtc()
      .domain([startDate, endDate])
      .range([margin.left, width - margin.right])
      .clamp(true);

    const xAxis = (g) =>
      g
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(
          //d3.axisBottom(x).tickFormat(d3.timeFormat("%b-%y")).tickSizeOuter(0).ticks(20)
          d3.axisBottom(x).tickSizeOuter(0)
        );
    axisGroup.append("g").call(xAxis);

    const yRight = d3
      .scaleLinear()
      .domain([0, maxCases * relaxFactor])
      .range([height - margin.bottom, margin.top]);

    const yAxisRight = (g) =>
      g
        .attr("transform", `translate(${width - margin.right},0)`)
        .call(d3.axisRight(yRight).tickSizeOuter(0))
        .call((g) =>
          g
            .append("text")
            .attr("dx", -10)
            // .attr("x", 0)
            .attr("y", margin.top + 12)
            .attr("fill", "#000")
            .attr("opacity", 0.9)
            .attr("font-size", 12)
            .attr("text-anchor", "end")
            .text(labels.rightAxis)
        );

    const yLeft = d3
      .scaleLinear()
      .domain([0, d3.max(series, (d) => d3.max(d, (d) => d[1])) * relaxFactor])
      .range([height - margin.bottom, margin.top]);
    axisGroup.append("g").call(yAxisRight);

    const yAxisLeft = (g) =>
      g
        .attr("transform", `translate(${margin.left},0)`)
        .call(d3.axisLeft(yLeft).tickSizeOuter(0))
        .call((g) =>
          g
            .append("text")
            .attr("dx", 10)
            .attr("y", margin.top + 12)
            .attr("fill", "#000")
            .attr("opacity", 0.9)
            .attr("font-size", 12)
            .attr("text-anchor", "start")
            .text(labels.leftAxis)
        );

    axisGroup.append("g").call(yAxisLeft);

    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => yRight(d.value))
      .curve(curve);

    const area = d3
      .area()
      .x((d) => x(d.data[0]))
      .y0((d) => yLeft(d[0]))
      .y1((d) => yLeft(d[1]))
      .curve(curve);

    // console.log(incidentData)

    const highlightArea = d3
      .area()
      .x((d) => x(d[0]))
      .y0(yLeft(0))
      .y1((d) => yLeft(d[1]))
      .curve(curve);

    const highlightAreaZero = d3
      .area()
      .x((d) => x(d[0]))
      .y0(yLeft(0))
      .y1(yLeft(0))
      .curve(d3.curveBasis);

    areaGroup
      .append("g")
      .selectAll("path")
      .data(series)
      .join("path")
      .attr("class", ({ key }) => `country-area ${key}`)
      .attr("fill", ({ key }) => colors[countries.indexOf(key)])
      .attr("opacity", 0.85)
      .attr("d", area)
      .append("title")
      .text(({ key }) => key);

    var locked = false;
    var lockedIndex = null;

    const legendDiv = d3.select(".legend");

    const legendEntry = legendDiv
      .selectAll(".legend-row")
      .data(countries)
      .enter()
      .append("div")
      .attr("class", (d, i) => `legend-row legend-row-${i}`);

    legendEntry
      .append("div")
      .attr("class", "legend-rect")
      .style("background", (d, i) => colors[i]);

    legendEntry
      .append("span")
      .attr("class", (d, i) => `legend-text legend-text-${i}`)
      .attr("content", (d) => d)
      .text((d) => d);

    function highlightSeries(d) {
      const i = countries.indexOf(d);

      d3.selectAll(".country-line")
        .transition()
        .duration(transDuration)
        .style("opacity", 0.05)
        .attr("stroke-width", baseStrokeWidth);
      d3.selectAll(".country-area")
        .transition()
        .duration(transDuration)
        .style("opacity", 0.1);
      d3.selectAll(".backdrop-line")
        .transition()
        .duration(transDuration)
        .style("opacity", 0);
      d3.selectAll(".legend-group").style("font-weight", 400);

      d3.select(`.legend-group.${d}`).style("font-weight", 700);
      d3.select(`.backdrop-line.backdrop-line-${i}`)
        .transition()
        .duration(transDuration)
        .style("opacity", 1);
      d3.select(`.country-line.country-line-${i}`)
        .raise()
        .transition()
        .duration(transDuration)
        .style("opacity", 1)
        .attr("stroke-width", highlightStrokeWidth);

      var highlightData = incidentData.map((item) => {
        return [item.date, item[d]];
      });

      areaGroup
        .append("g")
        .attr("class", "temp-highlight")
        .append("path")
        .attr("fill", colors[countries.indexOf(d)])
        .attr("d", highlightAreaZero(highlightData))
        .transition()
        .duration(transDuration)
        .attr("d", highlightArea(highlightData));
    }

    function unhiglightAll() {
      d3.selectAll(".legend-group").style("font-weight", 400);
      d3.selectAll(".country-line")
        .transition()
        .duration(transDuration)
        .style("opacity", 1)
        .attr("stroke-width", baseStrokeWidth);

      d3.selectAll(".country-area")
        .transition()
        .duration(transDuration)
        .style("opacity", 0.85);
      d3.selectAll(".backdrop-line")
        .transition()
        .duration(transDuration)
        .style("opacity", 0);

      d3.selectAll(".temp-highlight").remove();
    }

    legendEntry
      .on("mouseover", (event, d) => {
        if (locked) return;
        highlightSeries(d);
      })
      .on("mouseout", (event, d) => {
        if (locked) return;
        unhiglightAll();
      })
      .on("mousedown", (event, d) => {
        const clickedIndex = countries.indexOf(d);

        if (locked) {
          locked = clickedIndex === lockedIndex ? false : true;

          if (clickedIndex !== lockedIndex) {
            unhiglightAll();
            highlightSeries(d);
          }

          lockedIndex = clickedIndex === lockedIndex ? null : clickedIndex;
          d3.selectAll(".hover-group").remove();
        } else {
          locked = true;
          lockedIndex = clickedIndex;
        }

        const legendOpacity = locked ? 0.2 : 1;
        d3.selectAll(".legend-row")
          .transition()
          .duration(transDuration)
          .style("opacity", legendOpacity);

        if (locked)
          d3.select(`.legend-row.legend-row-${lockedIndex}`)
            .transition()
            .duration(transDuration)
            .style("opacity", 1);
      });

    const countryLine = lineGroup
      .selectAll(".backdrop-line")
      .data(countries)
      .enter()
      .append("path")
      .attr("class", (d, i) => `backdrop-line backdrop-line-${i}`)
      .attr("fill", "none")
      .attr("stroke-width", 5)
      .attr("stroke", "white")
      .style("opacity", 0)
      .attr("d", (d, i) =>
        line(
          casesData.map((item) => {
            return { date: item.date, value: item[d] };
          })
        )
      );

    countryLine
      .clone()
      .attr("class", (d, i) => `country-line country-line-${i}`)
      .attr("stroke", (d, i) => colors[i])
      .style("opacity", 1)
      .attr("stroke-width", baseStrokeWidth)
      .append("title")
      .text((d, i) => d);

    annotationsGroup
      .append("rect")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", width - margin.right - margin.left)
      .attr("height", height - margin.bottom - margin.top)
      .attr("fill", "red")
      .attr("opacity", 0)
      .attr("class", "mouseover")
      .on("mousemove", (event) => {
        if (!locked) return;
        const dates = incidentData.map((d) => d.date);
        var mouse = d3.pointer(event);

        const xm = x.invert(mouse[0]).getTime();
        const i1 = d3.bisectLeft(dates, xm, 1);
        const i0 = i1 - 1;
        const i = xm - dates[i0] > dates[i1] - xm ? i1 : i0;

        if (i >= 0 && i < incidentData.length) {
          d3.selectAll(".hover-group").remove();
          const incidentValue = incidentData[i][countries[lockedIndex]];
          const casesValue = casesData[i][countries[lockedIndex]];

          const hoverGroup = annotationsGroup
            .append("g")
            .attr("class", "hover-group");

          hoverGroup
            .append("text")
            .attr("x", x(incidentData[i].date))
            .attr("y", height + 14)
            .attr("text-anchor", "middle")
            .attr("font-family", "sans-serif")
            .attr("font-size", 10)
            .attr("opacity", 0.8)
            .text(`Incidents: ${d3.format(",")(incidentValue)}`);

          hoverGroup
            .append("text")
            .attr("x", x(incidentData[i].date))
            .attr("y", height + 28)
            .attr("text-anchor", "middle")
            .attr("font-family", "sans-serif")
            .attr("font-size", 10)
            .attr("opacity", 0.8)
            .text(`Cases: ${d3.format(",")(casesValue)}`);

          hoverGroup
            .append("circle")
            .attr("fill", colors[lockedIndex])
            .attr("stroke", "white")
            .attr("stroke-width", 1.5)
            .attr("r", 5)
            .attr("cx", x(incidentData[i].date))
            .attr("cy", yLeft(incidentValue))
            .attr(
              "opacity",
              selectedOption === "optionAll" ||
                selectedOption === "optionIncidents"
                ? 1
                : 0
            );

          hoverGroup
            .append("circle")
            .attr("fill", colors[lockedIndex])
            .attr("stroke", "white")
            .attr("stroke-width", 1.5)
            .attr("r", 5)
            .attr("cx", x(casesData[i].date))
            .attr("cy", yRight(casesValue))
            .attr(
              "opacity",
              selectedOption === "optionAll" || selectedOption === "optionCases"
                ? 1
                : 0
            );

          const incidentText = hoverGroup
            .append("g")
            .attr("transform", `translate(0,-15)`);

          incidentText
            .append("text")
            .attr("x", x(incidentData[i].date))
            .attr("y", yLeft(incidentValue))
            .attr("text-anchor", "middle")
            .attr("stroke", "white")
            .attr("stroke-width", 4)
            .attr(
              "opacity",
              selectedOption === "optionAll" ||
                selectedOption === "optionIncidents"
                ? 1
                : 0
            )
            .text(d3.format(",")(incidentValue));

          incidentText
            .append("text")
            .attr("text-anchor", "middle")
            .attr("x", x(incidentData[i].date))
            .attr("y", yLeft(incidentValue))
            .attr(
              "opacity",
              selectedOption === "optionAll" ||
                selectedOption === "optionIncidents"
                ? 1
                : 0
            )
            .text(d3.format(",")(incidentValue));

          const casesText = hoverGroup
            .append("g")
            .attr("transform", `translate(0,-15)`);

          casesText
            .append("text")
            .attr("x", x(casesData[i].date))
            .attr("y", yRight(casesValue))
            .attr("text-anchor", "middle")
            .attr("stroke", "white")
            .attr("stroke-width", 4)
            .attr(
              "opacity",
              selectedOption === "optionAll" || selectedOption === "optionCases"
                ? 1
                : 0
            )
            .text(d3.format(",")(casesValue));

          casesText
            .append("text")
            .attr("text-anchor", "middle")
            .attr("x", x(casesData[i].date))
            .attr("y", yRight(casesValue))
            .attr(
              "opacity",
              selectedOption === "optionAll" || selectedOption === "optionCases"
                ? 1
                : 0
            )
            .text(d3.format(",")(casesValue));
        }
      });

    const annotationRects = annotationsGroup
      .selectAll("circle")
      .data(annotations)
      .enter()
      .append("g");

    annotationRects
      .append("rect")
      .attr("width", 20)
      .attr("height", 20)
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .attr("fill", "#959595")
      .style("filter", "url(#drop-shadow)")
      .on("mouseover", (event, d) => {
        flagLine
          .attr("opacity", 1)
          .attr("x1", x(d.date) - 14)
          .attr("x2", x(d.date) - 14);

        var fObject = annotationsGroup
          .append("foreignObject")
          .attr("class", "svg-tooltip")
          .attr("x", () => {
            let offset = x(d.date) - 14;
            if (offset + tooltipWidth >= width - margin.left - margin.right)
              offset -= tooltipWidth;
            return offset;
          })
          .attr("y", yLeft(0) - 1.5 * tooltipHeight)
          .attr("width", tooltipWidth)
          .attr("height", tooltipHeight);

        var fo = fObject
          .append("xhtml:body")
          .attr("xmlns", "http://www.w3.org/1999/xhtml");

        var h1 = fo.append("xhtml:h1");
        h1.attr("class", "h1").html(d3.timeFormat("%B %d %Y")(d.date));

        fo.append("xhtml:hr");

        var div = fo.append("xhtml:div");
        div.append("xhtml:p").html(d.annotation);
      })
      .on("mouseout", () => {
        svg.selectAll(".svg-tooltip").remove();
        flagLine.attr("opacity", 0);
      });

    annotationRects.attr(
      "transform",
      (d) =>
        `translate(${x(d.date) - 28}, ${height - margin.bottom}) rotate(-45)`
    );

    const plusSign = annotationRects.append('g').attr("transform",'rotate(45)');
    plusSign
      .append("text")
      .attr('pointer-events', 'none')
      .attr("font-size", 22)
      .attr("fill", "white")
      // .attr('text-anchor', 'middle')
      .attr('dx', 8)
      .attr('dy', 5)
      .text("+");


  }, [wrapDim]);

  return (
    <div>
      <div className="controls">
        <div className="legend"></div>
        <div>
          {/* <span className="label">Select</span> */}
          <div className="dropdown">
            <button id="showOptions" className="dropdown__btn">
              Show incidents and cases
            </button>
            <div className="dropdown__list">
              <a id="optionAll" href="#" className="dropdown__link selected">
                Show incidents and cases
              </a>
              <a id="optionIncidents" href="#" className="dropdown__link">
                Show only incidents
              </a>
              <a id="optionCases" href="#" className="dropdown__link">
                Show only cases
              </a>
            </div>
          </div>
        </div>
      </div>

      <div ref={wrapperRef} className="matrix-wrapper">
        <div className="chartWrapper">
          <svg ref={svgRef}></svg>
        </div>
      </div>
    </div>
  );
}

export default LineAreaCombo;
